import { Component } from '@theme/component';

/**
 * Selector cascade used to find the product gallery to scroll to.
 *
 * Kept byte-for-byte identical to the cascade in `assets/oc-pdp-hero.js`: both sections drive the
 * same "Check Availability" behaviour and must resolve to the same element. It is duplicated
 * rather than shared because Horizon resolves module specifiers through the import map declared in
 * `layout/theme.liquid`, and a new shared module would have to be registered there.
 *
 * Ordered from the most explicit to the loosest. The first two entries are the ids Dawn prints on
 * its gallery wrappers. The next entries are Horizon's own product gallery:
 * `assets/media-gallery.js` + the `.media-gallery--carousel` wrapper in `assets/base.css`, the
 * `_product-media-gallery` block rendered by `sections/product-information.liquid`, and the
 * `.product-media` / `.product-media-container` wrappers. The last two are Dawn's outer wrappers.
 *
 * @type {string[]}
 */
const GALLERY_SELECTORS = [
  '[id^="MediaGallery-"]',
  '[id^="GalleryViewer-"]',
  'media-gallery',
  '.media-gallery',
  'product-media-gallery',
  '.product-media-gallery',
  '.product-media-container',
  '.product-media',
  '.product__media-wrapper',
  '.product__info-wrapper',
];

/**
 * Resolves the element the "Check Availability" call to action should scroll to.
 *
 * Tries the merchant-provided selector first, then the cascade above. Every lookup is guarded, so
 * a malformed selector degrades to the next candidate instead of throwing.
 *
 * @param {string} custom - Merchant selector coming from the `scroll_target` setting.
 * @returns {Element | null}
 */
export function resolveGalleryTarget(custom) {
  const selectors = custom ? [custom, ...GALLERY_SELECTORS] : GALLERY_SELECTORS;

  for (const selector of selectors) {
    try {
      const element = document.querySelector(selector);

      if (element) return element;
    } catch (error) {
      /* invalid selector — fall through to the next candidate */
    }
  }

  return null;
}

/**
 * Scrolls to `target`, honouring `prefers-reduced-motion`.
 *
 * @param {Element} target
 */
export function scrollToTarget(target) {
  const reduced =
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
}

/**
 * Sticky-style call to action band.
 *
 * Same single behaviour as the PDP hero: the call to action scrolls to the product gallery instead
 * of navigating. If no gallery can be found the click is left alone and the anchor keeps its
 * default behaviour (it points at the product URL).
 *
 * @extends {Component}
 */
export class OcCtaBandComponent extends Component {
  /**
   * @param {Event} event - The click on the call to action.
   */
  scrollToGallery(event) {
    try {
      const target = resolveGalleryTarget((this.dataset.scrollTarget || '').trim());

      if (!target) return;

      event.preventDefault();

      scrollToTarget(target);
    } catch (error) {
      /* never break the page — the anchor keeps its default behaviour */
    }
  }
}

if (!customElements.get('oc-cta-band-component')) {
  customElements.define('oc-cta-band-component', OcCtaBandComponent);
}
